// src/features/editor/components/studio/EditorWorkspace.tsx
import { useKeyboardAvoid } from './useKeyboardAvoid'
import { useEffect } from 'react'
import { cn } from '@/lib/utils'
import type { CourseWithoutBlocks } from '@/types/course'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import {
  resetEditor,
  seedContentLang,
  setBlockSheetOpen,
  setPanelOpen,
} from '@/features/editor/editorSlice'
import { seedLangFor } from './seedContentLang'
import { useViolationJumpParams } from './useViolationJumpParams'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { CourseSettingsDialog } from '@/features/editor/components/CourseSettingsDialog'
import { LessonPreviewDialog } from '@/features/editor/components/preview/LessonPreviewDialog'
import { PublishDialog } from '@/features/editor/components/publish/PublishDialog'
import { TextEditorProvider } from '@/features/editor/components/blocks/previews/design/TextEditorContext'
import { UploadQueueProvider } from '@/features/editor/components/shared/UploadQueueContext'
import { VideoUploadProvider } from '@/features/editor/components/shared/VideoUploadContext'
import { AISidebar } from './AISidebar'
import { AiSidebarToggle } from './AiSidebarToggle'
import { BlockDraftProvider } from './BlockDraftContext'
import { CourseTimeline } from './CourseTimeline'
import { DrawImagesDialog } from './DrawImagesDialog'
import { EditorHeader } from './EditorHeader'
import { CANVAS_PR_CLASS } from './layout'
import { BlockPanelSheet } from './BlockPanelSheet'
import { CanvasTools } from './CanvasTools'
import { DeselectOnEmptyClick } from './DeselectOnEmptyClick'
import { useRememberedLesson } from './useRememberedLesson'
import { usePrefetchLessonBlocks } from './usePrefetchLessonBlocks'
import { CANVAS_ADD_SLOT_ID } from '../blocks/AddBlockMenu'
import { MobilePreviewFrame } from './MobilePreviewFrame'

/** Редактор загруженного курса. Загрузку и выбор источника (черновик/живой)
 *  ведёт `EditorShell`; сюда курс приходит уже с живым `id`. */
export const EditorWorkspace = ({ course }: { course: CourseWithoutBlocks }) => {
  const dispatch = useAppDispatch()
  const aiOpen = useAppSelector((s) => s.editor.panels.aiSidebarOpen)
  const blockOpen = useAppSelector((s) => s.editor.panels.blockPanelOpen)
  const drawImagesSlot = useAppSelector((s) => s.editor.drawImagesSlot)
  const previewOpen = useAppSelector((s) => s.editor.dialogs.previewOpen)
  // Уже `lg` панель экрана выезжает по кнопке; сняли выбор — закрылась и она.
  const sheetOpen = useAppSelector((s) => s.editor.blockSheetOpen)
  const setSheetOpen = (open: boolean) => dispatch(setBlockSheetOpen(open))
  useEffect(() => {
    if (!blockOpen) dispatch(setBlockSheetOpen(false))
  }, [blockOpen, dispatch])

  useEffect(() => () => void dispatch(resetEditor()), [dispatch])
  useRememberedLesson(course)
  usePrefetchLessonBlocks(course)

  // Засев один раз на курс, по id: иначе приехавший после PUT курс отщёлкивал бы
  // переключатель под руками автора. Уход из редактора чистит resetEditor().
  useEffect(() => {
    // Засев запоминается: по нему места промпта отличают исходный слот от
    // второго, который правится, но авто-режимом не рисуется (§5.3).
    dispatch(seedContentLang(seedLangFor(course)))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- намеренно по id, а не по объекту курса
  }, [course.id, dispatch])

  // После засева: выбор урока и блока из «Перейти» на странице настроек.
  useViolationJumpParams()

  const canvasRef = useKeyboardAvoid()

  const toggleAiSidebar = () => dispatch(setPanelOpen({ panel: 'aiSidebarOpen', open: !aiOpen }))

  return (
    <ErrorBoundary>
      {/* Ref parity: horizontal root — AI sidebar is a full-height left sibling; the header
          and timeline live inside the main content column (right of the sidebar). */}
      {/* `h-dvh`, а не `h-screen`: на iPhone 100vh выше видимой области, и низ
          редактора (лента миниатюр) прятался под панелью браузера. */}
      <div className="relative flex h-dvh bg-surface">
        {/* Diamond logo — fixed top-left, toggles the AI sidebar */}
        <AiSidebarToggle open={aiOpen} onToggle={toggleAiSidebar} />

        {/* Провайдер поднят над AISidebar (E8), DrawImagesDialog (E9) и
            CourseSettingsDialog (F6, вкладка «Языки» — третья платная точка
            старта): черновик выделенного блока — один инстанс на редактор, и
            всем трём точкам старта запуска нужен его реальный `isDirty`, а не
            дефолт контекста, чтобы гасить старт поверх несохранённой правки.
            Radix портирует содержимое диалога в конец `body`, поэтому
            вложенность в дереве React на вёрстку не влияет — вложенность
            здесь только про контекст. Сторож на регресс положения —
            `EditorShell.test.tsx` (тест без мока `BlockDraftContext`). */}
        {/* Очередь загрузок (п.24) — ВНУТРИ `BlockDraftProvider`: её гейты
            читают узкую проекцию черновика (`useBlockDraftGate`), а не полное
            значение контекста, которое меняется на каждое нажатие клавиши. */}
        <BlockDraftProvider courseId={course.id}>
          <UploadQueueProvider courseId={course.id}>
            {/* Загрузки ролика — рядом с очередью картинок и по той же причине:
                переживают уход из видео-блока, гейты читают черновик выше. */}
            <VideoUploadProvider courseId={course.id}>
              {/* Всегда в дереве: реф анимирует ширину 380→0, а история чата переживает сворачивание. */}
              <AISidebar course={course} />

              <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
                <EditorHeader course={course} aiOpen={aiOpen} />
                {/* Both the design-block canvas (RichTextEditor) and the settings panel
                (TextFormatToolbar) need the same text-editor context, so the provider
                wraps this canvas+panel row from above. */}
                <TextEditorProvider>
                  <div className="relative flex min-h-0 flex-1 overflow-hidden bg-surface">
                    {/* Холст во всю ширину; панель блока с `lg` — карточка справа поверх холста,
                    уже 1024 px — выезжает справа по кнопке на холсте (`BlockPanelSheet`). */}
                    <div
                      ref={canvasRef}
                      className={cn(
                        'mb-2 ml-2 flex min-h-0 flex-1 overflow-hidden rounded-2xl bg-background transition-all duration-300',
                        // Панель блока выезжает из правого края: под ней холст тоже уходит
                        // в край без скругления, иначе из-за панели торчали бы его углы.
                        blockOpen ? cn(CANVAS_PR_CLASS, 'mr-2 lg:mr-0 lg:rounded-r-none') : 'mr-2',
                      )}
                    >
                      {/* Отменить / вернуть / звук — в правом нижнем углу холста (а не в шапке). */}
                      <DeselectOnEmptyClick className="relative flex min-w-0 flex-1">
                        <MobilePreviewFrame course={course} />
                        {/* Слот выбора типа нового экрана — по центру холста (`AddBlockMenu`). */}
                        <div id={CANVAS_ADD_SLOT_ID} className="contents" />
                        <CanvasTools
                          course={course}
                          // Телефон: «Настройки экрана» над холстом нет — урок открывается шестерёнкой
                          // на плашке, экран — повторным тапом по миниатюре.
                          onOpenPanel={null}
                        />
                      </DeselectOnEmptyClick>
                    </div>
                    {blockOpen ? (
                      <BlockPanelSheet
                        course={course}
                        sheetOpen={sheetOpen}
                        onCloseSheet={() => setSheetOpen(false)}
                      />
                    ) : null}
                  </div>
                </TextEditorProvider>
                <CourseTimeline course={course} />
              </div>

              {/* Слот открытия: шапка редактора открывает диалог базовым (`null`),
              итог перевода — вторым языком (§7.4, четвёртая ветка). */}
              <DrawImagesDialog course={course} initialSlot={drawImagesSlot ?? undefined} />
              <CourseSettingsDialog course={course} />
              {/* Только пока открыт: прохождение обязано умирать вместе с
                диалогом, а смонтированный всегда — он читает ПОЛНЫЙ контекст
                черновика и перерисовывался бы на каждое нажатие клавиши. */}
              {previewOpen ? <LessonPreviewDialog course={course} /> : null}
            </VideoUploadProvider>
          </UploadQueueProvider>
          {/* Внутри провайдера ради `isDirty` в «Отменить правки» (сторож — discardDraft.test). */}
          <PublishDialog course={course} />
        </BlockDraftProvider>
      </div>
    </ErrorBoundary>
  )
}
